import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Package, ShoppingCart, Plus, Edit, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const TABS = ["orders", "products"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product or null
  const [authError, setAuthError] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("be_admin_token") : null;

  const fetchAll = useCallback(async () => {
    try {
      const [oRes, pRes] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/products"),
      ]);
      setOrders(oRes.data);
      setProducts(pRes.data);
    } catch (e) {
      if (e?.response?.status === 401) setAuthError(true);
    }
  }, []);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  if (!token || authError) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem("be_admin_token");
    navigate("/admin/login");
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveProduct = async (form) => {
    try {
      if (form.id) {
        await api.put(`/admin/products/${form.id}`, {
          name: form.name,
          description: form.description,
          price: parseFloat(form.price),
          category: form.category,
          image_url: form.image_url,
          unit: form.unit,
          featured: !!form.featured,
          in_stock: !!form.in_stock,
        });
        toast.success("Product updated");
      } else {
        await api.post(`/admin/products`, {
          name: form.name,
          description: form.description,
          price: parseFloat(form.price),
          category: form.category,
          image_url: form.image_url,
          unit: form.unit,
          featured: !!form.featured,
          in_stock: !!form.in_stock,
        });
        toast.success("Product added");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const revenue = paidOrders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap justify-between items-end border-b border-ink-600 pb-6 mb-8">
        <div>
          <div className="text-brand font-display text-sm tracking-[0.4em] mb-2">/ ADMIN</div>
          <h1 className="font-display text-5xl text-cream leading-none">PIT CONTROL</h1>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 px-4 h-11 border-2 border-ink-600 hover:border-[#D9381E] hover:text-[#D9381E] text-cream font-display text-lg tracking-widest"
        >
          <LogOut size={16} /> LOGOUT
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label="TOTAL ORDERS" value={orders.length} />
        <Stat label="PAID ORDERS" value={paidOrders.length} accent />
        <Stat label="REVENUE" value={`$${revenue.toFixed(2)}`} accent />
        <Stat label="PRODUCTS" value={products.length} />
      </div>

      <div className="flex gap-8 border-b border-ink-600 mb-8">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`pb-3 font-display text-2xl tracking-widest border-b-2 -mb-px transition ${
              tab === t ? "text-brand border-brand" : "text-cream border-transparent hover:text-brand"
            }`}
          >
            {t === "orders" ? <span className="flex items-center gap-2"><ShoppingCart size={18} /> ORDERS</span> : <span className="flex items-center gap-2"><Package size={18} /> PRODUCTS</span>}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="overflow-x-auto border border-ink-600">
          <table className="w-full text-left text-sm">
            <thead className="bg-ink-800 text-[#A19D94] uppercase text-xs tracking-widest">
              <tr>
                <th className="p-3">Order</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Pickup</th>
                <th className="p-3">Items</th>
                <th className="p-3">Total</th>
                <th className="p-3">Payment</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-[#A19D94]">No orders yet.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-ink-600 hover:bg-ink-800/50" data-testid={`order-row-${o.id}`}>
                  <td className="p-3 text-cream font-mono text-xs">{o.id.slice(0, 8)}</td>
                  <td className="p-3 text-cream">
                    <div>{o.customer_name}</div>
                    <div className="text-[#A19D94] text-xs">{o.customer_email}</div>
                    <div className="text-[#A19D94] text-xs">{o.customer_phone}</div>
                  </td>
                  <td className="p-3 text-cream">{o.pickup_date} @ {o.pickup_time}</td>
                  <td className="p-3 text-cream">{o.items?.reduce((s, i) => s + i.quantity, 0)}</td>
                  <td className="p-3 text-cream font-display text-lg">${o.total?.toFixed(2)}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs tracking-widest ${o.payment_status === "paid" ? "bg-brand/20 text-brand border border-brand/50" : "bg-ink-700 text-[#A19D94] border border-ink-600"}`}>
                      {o.payment_status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3 text-cream uppercase text-xs tracking-widest">{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              data-testid="admin-add-product"
              onClick={() => setEditing({ name: "", description: "", price: "", category: "meats", image_url: "", unit: "each", featured: false, in_stock: true })}
              className="inline-flex items-center gap-2 px-4 h-11 bg-brand text-ink-900 font-display text-lg tracking-widest hover:bg-brand-hover"
            >
              <Plus size={16} /> ADD PRODUCT
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="border border-ink-600 bg-ink-800 p-4 flex gap-4">
                <img src={p.image_url} alt="" className="w-20 h-20 object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl text-cream truncate">{p.name}</div>
                  <div className="text-xs text-[#A19D94]">{p.category} • {p.unit}</div>
                  <div className="text-brand font-display text-lg mt-1">${p.price.toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 border border-ink-600 hover:border-brand hover:text-brand"><Edit size={14} /></button>
                  <button data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 border border-ink-600 hover:border-[#D9381E] hover:text-[#D9381E]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSave={saveProduct} />}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`border p-5 ${accent ? "border-brand/40 bg-brand/5" : "border-ink-600 bg-ink-800"}`} data-testid="admin-stat">
    <div className="text-xs tracking-[0.3em] text-[#A19D94] uppercase">{label}</div>
    <div className={`font-display text-4xl mt-2 leading-none ${accent ? "text-brand" : "text-cream"}`}>{value}</div>
  </div>
);

const ProductEditor = ({ product, onClose, onSave }) => {
  const [form, setForm] = useState(product);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-editor">
      <div className="bg-ink-800 border border-ink-600 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl text-cream">{product.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</h2>
          <button onClick={onClose} data-testid="editor-close"><X size={20} className="text-cream" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {[
            ["name", "Name", "input"],
            ["price", "Price", "number"],
            ["unit", "Unit", "input"],
            ["category", "Category", "select"],
            ["image_url", "Image URL", "input", "col-span-2"],
            ["description", "Description", "textarea", "col-span-2"],
          ].map(([k, label, type, cls = ""]) => (
            <label key={k} className={`block ${cls}`}>
              <span className="text-xs tracking-widest text-[#A19D94] uppercase">{label}</span>
              {type === "textarea" ? (
                <textarea rows={3} data-testid={`editor-${k}`} value={form[k] || ""} onChange={(e) => upd(k, e.target.value)} className="mt-2 w-full bg-ink-900 border border-ink-600 text-cream px-3 py-2 focus:border-brand focus:outline-none" />
              ) : type === "select" ? (
                <select data-testid={`editor-${k}`} value={form[k] || "meats"} onChange={(e) => upd(k, e.target.value)} className="mt-2 w-full bg-ink-900 border border-ink-600 text-cream px-3 py-2 focus:border-brand focus:outline-none">
                  <option value="meats">meats</option>
                  <option value="sausages">sausages</option>
                  <option value="ready-meals">ready-meals</option>
                  <option value="rubs">rubs</option>
                  <option value="sauces">sauces</option>
                </select>
              ) : (
                <input type={type === "number" ? "number" : "text"} step="0.01" data-testid={`editor-${k}`} value={form[k] ?? ""} onChange={(e) => upd(k, e.target.value)} className="mt-2 w-full bg-ink-900 border border-ink-600 text-cream px-3 py-2 focus:border-brand focus:outline-none" />
              )}
            </label>
          ))}
          <label className="flex items-center gap-2 text-cream">
            <input type="checkbox" data-testid="editor-featured" checked={!!form.featured} onChange={(e) => upd("featured", e.target.checked)} className="accent-brand" />
            <span className="font-display tracking-widest">FEATURED</span>
          </label>
          <label className="flex items-center gap-2 text-cream">
            <input type="checkbox" data-testid="editor-in-stock" checked={form.in_stock !== false} onChange={(e) => upd("in_stock", e.target.checked)} className="accent-brand" />
            <span className="font-display tracking-widest">IN STOCK</span>
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-6 h-11 border-2 border-ink-600 hover:border-cream text-cream font-display tracking-widest">CANCEL</button>
          <button data-testid="editor-save" onClick={() => onSave(form)} className="px-6 h-11 bg-brand hover:bg-brand-hover text-ink-900 font-display tracking-widest">SAVE</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
